import { FC, useMemo } from 'react';
import classNames from 'classnames';

import lang from '../../../lib/language';

import GoBackButton from '../../../components/GoBackButton/GoBackButton';
import Grid from '../../../components/Grid/Grid';
import NavButtons from '../../../components/NavigationButtons/NavButtons';
import SymbolTable from '../../../components/SymbolTable/SymbolTable';
import { SymbolTableGrid } from '../../../components/SymbolTable/SymbolTableGrid.enum';
import useIsMobile from '../../hooks/useIsMobile';
import NoDataAvailable from '../../NoDataAvailable/NoDataAvailable';
import PopularGroupTableItem from '../../PopularGroups/components/PopularGroupTableItem/PopularGroupTableItem';
import { PopularGroupItemRow } from '../../PopularGroups/PopularGroups.types';
import { WEEK_52_HIGH_LOW_TAB_STATE_KEY } from '../Week52GainersAndLosers.constants';

import { Week52GainersAndLosersInternalProps } from './Week52GainersAndLosersInternal.types';

import styles from './Week52GainersAndLosersInternal.module.scss';

const Week52GainersAndLosersInternal: FC<Week52GainersAndLosersInternalProps> = ({
  rows,
  activeTabIndex,
  isReady,
  isLoading,
  isNoData,
  hasMorePages,
  pageSize,
  enableRowToggle,
  isAce,
  titleRef,
  onTabChange,
  onLoadMore,
  onSymbolClick,
  onGoBack,
}: Week52GainersAndLosersInternalProps): JSX.Element => {
  const { isMobile } = useIsMobile();

  const tabs = [
    { text: lang.commonEpicMoversGainers(), component: <></> },
    { text: lang.commonEpicMoversLosers(), component: <></> },
  ];

  const tableHeaders = useMemo(() => {
    if (isNoData) return null;

    const headers = isMobile
      ? [
        lang.headerAccountBreakdownSymbolName(),
        lang.commonLastPrice(),
        lang.commonDiscover52WeekPercentChangeMobile(),
      ]
      : [
        lang.headerAccountBreakdownSymbolName(),
        lang.commonChart(),
        lang.commonLastPrice(),
        lang.commonDiscoverDailyPercentChange(),
        lang.commonDiscover52WeekPercentChange(),
      ];

    return (
      <div className={styles.tableHeaderRow}>
        {headers.map((item: string) => (
          <div key={item} className={styles.tableHeaderCell}>
            <span>{item}</span>
          </div>
        ))}
      </div>
    );
  }, [isMobile, isNoData]);

  return (
    <Grid>
      <div className={classNames(styles.wrapper, { [styles.wrapperAce]: isAce })}>
        {!isMobile && (
          <div className={styles.goBackButton}>
            <GoBackButton onBackBtnPress={onGoBack} />
          </div>
        )}
        <div className={styles.titleSection}>
          <div ref={titleRef} className={styles.title}>
            {lang.commonWeek52GainersAndLosers()}
          </div>
          <div className={styles.subtitle}>
            {lang.commonWeek52GainersAndLosersStockMakingHighLow()}
          </div>
        </div>
        <div className={styles.tableWrapper}>
          <NavButtons
            tabs={tabs}
            sendCurrentTabIndex={onTabChange}
            initialActiveTab={activeTabIndex}
            persistTabState={WEEK_52_HIGH_LOW_TAB_STATE_KEY}
          />
          <div className={styles.table}>
            {isNoData && (
              <NoDataAvailable
                className={styles.noDataToDisplay}
                isNoDataIcon
                title={lang.commonDiscover52WeekNoData()}
              />
            )}
            <SymbolTable
              items={rows}
              renderHeaders={() => tableHeaders}
              skeletonCount={pageSize}
              isLoading={isLoading}
              isWithMobileHeaders
              skeletonGrid={SymbolTableGrid.FIVE_ROW_ITEMS_GRID}
              renderRow={(row: PopularGroupItemRow) => (
                <PopularGroupTableItem
                  isWeek52Item
                  key={row.item.symbol}
                  row={row}
                  enableToggle={enableRowToggle}
                  onSymbolClick={onSymbolClick}
                />
              )}
            />
            {hasMorePages && (
              <div className={styles.viewMore}>
                <button
                  type="button"
                  onClick={onLoadMore}
                  className={styles.viewMoreButton}
                  disabled={!isReady}
                >
                  {lang.commonLoadMore()}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default Week52GainersAndLosersInternal;
